import * as React from 'react'
import {
  isTabularConfigName,
  MAX_TABULAR_INPUT_FILES_PER_RUN,
  TABULAR_CONFIG_NAME_HELP
} from '@atbash-labs/ontology-core'
import {
  useGetIngestMappingConfigQuery,
  useListIngestMappingConfigsQuery,
  useListTabularFileNameMatchesQuery
} from '@axilon-apps/domains-ontology-ui-state'
import { AlertTriangle } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Textarea } from '../../../components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group'
import { ConfigPicker, type SelectedTabularConfig } from './ConfigPicker'
import { specFor } from './constants'
import { fileMatchesAccept, tabularFileCountOk, tabularReplaceState } from './plan'
import { ReplaceConfirmDialog } from '../../../components/ReplaceConfirmDialog'
import { UpsertConfirmDialog } from './UpsertConfirmDialog'
import type { IngestWizardApi } from './useIngestWizard'

const BUILD_ACCEPT = specFor('tabular').drop?.accept ?? ''

export function TabularFileFields({ wizard }: { wizard: IngestWizardApi }) {
  const prep = wizard.state.prep.tabular
  const fields = prep?.fields ?? {}
  const files = prep?.files.length ? prep.files : prep?.file ? [prep.file] : []
  const action = fields.action === 'build' ? 'build' : fields.action === 'add' ? 'add' : ''
  const configsQuery = useListIngestMappingConfigsQuery({
    kind: 'mapped_tabular_ingest',
    latest: true,
    limit: 500
  })
  const selectedConfigQuery = useGetIngestMappingConfigQuery(fields.configId ?? '', {
    skip: !fields.configId
  })
  const [upsertOpen, setUpsertOpen] = React.useState(false)
  const definition = selectedConfigQuery.data?.definition
  const upsertTables = (definition?.output.tables ?? [])
    .filter((table) => table.mode === 'upsert')
    .map((table) => ({ table: table.table, key: table.key }))
  const allowedExtensions = definition?.input?.accept ?? []
  const invalidFile =
    action === 'add'
      ? files.find((file) => !fileMatchesAccept(file.name, allowedExtensions.join(',')))
      : action === 'build'
        ? files.find((file) => !fileMatchesAccept(file.name, BUILD_ACCEPT))
        : undefined
  const duplicateName =
    action === 'build' &&
    !!fields.configName &&
    (configsQuery.data ?? []).some((config) => config.name === fields.configName.trim())
  const nameInvalid =
    action === 'build' &&
    !!fields.configName?.trim() &&
    !isTabularConfigName(fields.configName.trim())
  const filesValid =
    action === 'build'
      ? files.length >= 1 && files.length <= 5 && !invalidFile && !duplicateName
      : action === 'add'
        ? tabularFileCountOk(files.length) && !invalidFile && !!definition
        : false

  React.useEffect(() => {
    const next = filesValid ? 'true' : 'false'
    if (fields.filesValid !== next) {
      wizard.updatePrep('tabular', { fields: { filesValid: next } })
    }
  }, [fields.filesValid, filesValid, wizard])

  const fileSignature = files.map((file) => `${file.name}:${file.size}`).join('|')
  const [replaceOpen, setReplaceOpen] = React.useState(false)
  const lookupNames = action === 'add' && filesValid ? files.map((file) => file.name) : []
  const matchesQuery = useListTabularFileNameMatchesQuery(
    { configName: fields.configName ?? '', names: lookupNames },
    { skip: lookupNames.length === 0 || !fields.configName, refetchOnMountOrArgChange: true }
  )
  const replaced = lookupNames.length > 0 ? (matchesQuery.currentData ?? []) : []
  const replaceSignature = `${fields.configName ?? ''}|${fileSignature}`
  const replaceConfirmed = tabularReplaceState({
    lookup:
      lookupNames.length === 0 || !fields.configName
        ? 'none'
        : matchesQuery.isFetching
          ? 'checking'
          : matchesQuery.isError
            ? 'failed'
            : 'done',
    matched: replaced.length > 0,
    signature: replaceSignature,
    confirmedFor: fields.replaceConfirmedFor
  })
  React.useEffect(() => {
    if ((fields.replaceConfirmed ?? '') !== replaceConfirmed) {
      wizard.updatePrep('tabular', { fields: { replaceConfirmed } })
    }
  }, [fields.replaceConfirmed, replaceConfirmed, wizard])
  const lastFileSignature = React.useRef(fileSignature)
  React.useEffect(() => {
    if (lastFileSignature.current === fileSignature) return
    lastFileSignature.current = fileSignature
    if (upsertTables.length > 0 && fields.upsertConfirmed === 'true') {
      wizard.updatePrep('tabular', { fields: { upsertConfirmed: 'required' } })
    }
  }, [fields.upsertConfirmed, fileSignature, upsertTables.length, wizard])

  const switchAction = (next: string) => {
    if (next !== 'build' && next !== 'add') return
    wizard.updatePrep('tabular', {
      fields: {
        action: next,
        configName: '',
        configId: '',
        configVersion: '',
        parserKind: '',
        description: '',
        upsertConfirmed: '',
        replaceConfirmed: '',
        replaceConfirmedFor: '',
        filesValid: 'false'
      }
    })
  }

  const selectConfig = (config: SelectedTabularConfig) => {
    const upserts = config.definition.output.tables.some((table) => table.mode === 'upsert')
    wizard.updatePrep('tabular', {
      fields: {
        configName: config.name,
        configId: config.id,
        configVersion: String(config.version),
        parserKind: config.parserKind,
        upsertConfirmed: upserts ? 'required' : ''
      }
    })
  }

  return (
    <div className="flex flex-col gap-3.5 rounded-md border border-line-2 bg-bg-sunk/40 p-3">
      <ToggleGroup
        type="single"
        value={action}
        onValueChange={switchAction}
        variant="outline"
        className="grid grid-cols-1 gap-2 sm:grid-cols-2"
      >
        <ToggleGroupItem value="build" className="h-auto min-h-12 flex-col items-start px-3 py-2">
          <span className="font-medium">Build a new config from examples</span>
          <span className="text-10 font-normal text-ink-3">
            Use one to five representative files.
          </span>
        </ToggleGroupItem>
        <ToggleGroupItem value="add" className="h-auto min-h-12 flex-col items-start px-3 py-2">
          <span className="font-medium">Add rows to an existing config</span>
          <span className="text-10 font-normal text-ink-3">
            Pin a saved config version; the files you add are parsed together in one run.
          </span>
        </ToggleGroupItem>
      </ToggleGroup>

      {action === 'build' ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="tabular-config-new-name">Config name</Label>
            <Input
              id="tabular-config-new-name"
              value={fields.configName ?? ''}
              placeholder="daily-production"
              aria-invalid={duplicateName || nameInvalid || undefined}
              onChange={(event) =>
                wizard.updatePrep('tabular', { fields: { configName: event.target.value } })
              }
            />
            <p
              className={
                duplicateName || nameInvalid ? 'text-10 text-st-down' : 'text-10 text-ink-3'
              }
            >
              {duplicateName
                ? 'A config with this name already exists.'
                : `${TABULAR_CONFIG_NAME_HELP}`}
            </p>
          </div>
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="tabular-config-description">What are these files? (optional)</Label>
            <Textarea
              id="tabular-config-description"
              rows={2}
              value={fields.description ?? ''}
              placeholder="One workbook per plant day; the production sheet is the one that matters."
              onChange={(event) =>
                wizard.updatePrep('tabular', { fields: { description: event.target.value } })
              }
            />
          </div>
        </div>
      ) : action === 'add' ? (
        <>
          <ConfigPicker
            value={{
              id: fields.configId,
              name: fields.configName,
              version: Number(fields.configVersion) || undefined
            }}
            onSelect={selectConfig}
          />
          {definition ? (
            <p className="text-xs text-ink-3" data-testid="tabular-add-summary">
              v{fields.configVersion} · {definition.parser.kind} parser · accepts{' '}
              {definition.input?.accept?.join(', ') || 'any'} · up to{' '}
              {MAX_TABULAR_INPUT_FILES_PER_RUN} files per run · writes to urn:db:{fields.configName}
              {files.length > 1 ? ` · ${files.length} files → 1 run` : ''}
            </p>
          ) : null}
        </>
      ) : (
        <p className="text-xs text-ink-3">Choose whether to build a config or ingest rows.</p>
      )}

      {action === 'add' && upsertTables.length > 0 ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-st-warn/30 bg-st-warn-bg px-3 py-2">
          <p className="text-xs text-st-warn">
            This config updates existing rows in {upsertTables.length} table
            {upsertTables.length === 1 ? '' : 's'}.
          </p>
          <Button type="button" size="sm" variant="outline" onClick={() => setUpsertOpen(true)}>
            {fields.upsertConfirmed === 'true' ? 'Upsert confirmed' : 'Confirm upsert…'}
          </Button>
        </div>
      ) : null}

      {action === 'add' && replaced.length > 0 ? (
        <div
          className="flex items-center justify-between gap-3 rounded-md border border-st-warn/30 bg-st-warn-bg px-3 py-2"
          data-testid="tabular-replace-warning"
        >
          <p className="min-w-0 text-xs break-words text-st-warn">
            {fields.configName} already has {replaced.length === 1 ? 'a file' : 'files'} named{' '}
            {replaced.map((file) => file.fileName).join(', ')}. Running replaces the rows{' '}
            {replaced.length === 1 ? 'it' : 'they'} wrote.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0"
            onClick={() => setReplaceOpen(true)}
          >
            {replaceConfirmed === 'true' ? 'Replace confirmed' : 'Confirm replace…'}
          </Button>
        </div>
      ) : null}

      {action && (!filesValid || invalidFile) ? (
        <div className="flex gap-2 rounded-md border border-st-warn/30 bg-st-warn-bg px-3 py-2 text-xs text-st-warn">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>
            {invalidFile
              ? `${invalidFile.name} is not accepted.`
              : action === 'build'
                ? 'Choose one to five files.'
                : files.length > MAX_TABULAR_INPUT_FILES_PER_RUN
                  ? `A run takes up to ${MAX_TABULAR_INPUT_FILES_PER_RUN} files; ${files.length} were added.`
                  : 'Choose one or more files; they are parsed together in one run.'}
          </span>
        </div>
      ) : null}

      <UpsertConfirmDialog
        open={upsertOpen}
        onOpenChange={setUpsertOpen}
        tables={upsertTables}
        onConfirm={() => {
          setUpsertOpen(false)
          wizard.updatePrep('tabular', { fields: { upsertConfirmed: 'true' } })
        }}
      />
      <ReplaceConfirmDialog
        open={replaceOpen}
        onOpenChange={setReplaceOpen}
        configName={fields.configName ?? ''}
        files={replaced}
        canRename
        onConfirm={() => {
          setReplaceOpen(false)
          wizard.updatePrep('tabular', { fields: { replaceConfirmedFor: replaceSignature } })
        }}
      />
    </div>
  )
}
